import { useEffect, useRef } from "react";

export const VOICE_DICTATION_SHORTCUT_EVENT = "chat-bridge:voice-dictation-shortcut";

export type VoiceDictationShortcutTarget = "quick-launch" | "normal-chat" | "thread-chat";

type VoiceDictationShortcutDetail = {
  target: VoiceDictationShortcutTarget;
};

/** True for the Cmd/Ctrl+E gesture shared by the voice-entry surfaces. */
export function isVoiceDictationShortcut(
  event: Pick<KeyboardEvent, "code" | "ctrlKey" | "metaKey" | "repeat">,
): boolean {
  return (
    (event.metaKey || event.ctrlKey) &&
    event.code === "KeyE" &&
    !event.repeat
  );
}

/** Ask one mounted composer to start the same flow as its mic button. */
export function dispatchVoiceDictationShortcut(target: VoiceDictationShortcutTarget): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<VoiceDictationShortcutDetail>(VOICE_DICTATION_SHORTCUT_EVENT, {
      detail: { target },
    }),
  );
}

/** Subscribe a composer to its scoped voice-dictation shortcut request. */
export function useVoiceDictationShortcut(
  target: VoiceDictationShortcutTarget | undefined,
  onStart: () => void,
): void {
  const onStartRef = useRef(onStart);

  useEffect(() => {
    onStartRef.current = onStart;
  }, [onStart]);

  useEffect(() => {
    if (!target || typeof window === "undefined") return;

    const handleShortcut = (event: Event) => {
      const detail = (event as CustomEvent<VoiceDictationShortcutDetail>).detail;
      if (detail?.target !== target) return;
      onStartRef.current();
    };

    window.addEventListener(VOICE_DICTATION_SHORTCUT_EVENT, handleShortcut);
    return () => window.removeEventListener(VOICE_DICTATION_SHORTCUT_EVENT, handleShortcut);
  }, [target]);
}
